import { useForm, Control, UseFormMethods, SubmitHandler } from 'react-hook-form';

import { useParams } from 'react-router-dom';
import { GqlUpdateWebCustomizationSettingsV2Input, RadiusOption, ProductCardButtonOption } from 'types/graphql';

import useErnie from 'shared/hooks/use-ernie';
import { PreviewData } from '../components/preview';
import { useUpdateWebCustomizationSettingsV2, MenuCustomizationData } from '../data-access';
import { ThemeFormProps } from './theme.types';

type HandlePublish = ReturnType<UseFormMethods<FormValues>['handleSubmit']>;

type FormValues = {
  buttonsLinks: string;
  mobileHeaderBackgroundColor?: string;
  mobileHeaderImageUrl?: string;
  mobileNavigationBarActiveColor?: string;
  mobileNavigationBarBackgroundColor?: string;
  mobileNavigationBarInactiveColor?: string;
  mobileTabLabelShop?: string;
  mobileTabLabelSpecials?: string;
  mobileTabLabelCart?: string;
  mobileTabLabelPay?: string;
  mobileTabLabelAccount?: string;
  navBar: string;
  staffPickTag: string;
  discountTag: string;
  defaultRadius: RadiusOption;
  productCardButton?: ProductCardButtonOption;
};

type Input = Pick<GqlUpdateWebCustomizationSettingsV2Input, 'colors' | 'mobile' | 'radius'>;

type UseThemeFormReturn = {
  control: Control<FormValues>;
  handlePublish: HandlePublish;
  handleReset: () => void;
  isDirty: boolean;
  previewData: PreviewData;
  applyAllInput: Input;
  afterApplyAll: () => void;
};

function valueOrFallback(value: string | null | undefined, fallback: string): string {
  return value?.length ? value : fallback;
}

function useMergeDataWithFormValues(this: void, data: MenuCustomizationData, formValues: FormValues): PreviewData {
  const { buttonsLinks, navBar, staffPickTag, discountTag, defaultRadius, productCardButton } = formValues;

  return {
    ...data,
    colors: {
      buttonsLinks,
      navBar,
      staffPickTag,
      discountTag,
      productCardButton: productCardButton ?? ProductCardButtonOption.tinted,
    },
    mobile: {
      headerBackgroundColor: valueOrFallback(
        formValues.mobileHeaderBackgroundColor,
        data.mobile?.headerBackgroundColor ?? ''
      ),
      headerImageUrl: valueOrFallback(formValues.mobileHeaderImageUrl, data.mobile?.headerImageUrl ?? ''),
      bottomBarActiveColor: valueOrFallback(
        formValues.mobileNavigationBarActiveColor,
        data.mobile?.bottomBarActiveColor ?? ''
      ),
      bottomBarBackgroundColor: valueOrFallback(
        formValues.mobileNavigationBarBackgroundColor,
        data.mobile?.bottomBarBackgroundColor ?? ''
      ),
      bottomBarInactiveColor: valueOrFallback(
        formValues.mobileNavigationBarInactiveColor,
        data.mobile?.bottomBarInactiveColor ?? ''
      ),
      tabLabels: {
        shop: valueOrFallback(formValues.mobileTabLabelShop, data.mobile?.tabLabels?.shop ?? ''),
        specials: valueOrFallback(formValues.mobileTabLabelSpecials, data.mobile?.tabLabels?.specials ?? ''),
        cart: valueOrFallback(formValues.mobileTabLabelCart, data.mobile?.tabLabels?.cart ?? ''),
        pay: valueOrFallback(formValues.mobileTabLabelPay, data.mobile?.tabLabels?.pay ?? ''),
        account: valueOrFallback(formValues.mobileTabLabelAccount, data.mobile?.tabLabels?.account ?? ''),
      },
    },
    radius: {
      default: defaultRadius,
    },
  };
}

function formatInput(values: FormValues): Input {
  return {
    colors: {
      buttonsLinks: values.buttonsLinks,
      navBar: values.navBar,
      staffPickTag: values.staffPickTag,
      discountTag: values.discountTag,
      productCardButton: values.productCardButton,
    },
    mobile: {
      headerBackgroundColor: values.mobileHeaderBackgroundColor,
      headerImageUrl: values.mobileHeaderImageUrl,
      bottomBarActiveColor: values.mobileNavigationBarActiveColor,
      bottomBarBackgroundColor: values.mobileNavigationBarBackgroundColor,
      bottomBarInactiveColor: values.mobileNavigationBarInactiveColor,
      tabLabels: {
        shop: values.mobileTabLabelShop,
        specials: values.mobileTabLabelSpecials,
        cart: values.mobileTabLabelCart,
        pay: values.mobileTabLabelPay,
        account: values.mobileTabLabelAccount,
      },
    },
    radius: {
      default: values.defaultRadius,
    },
  };
}

export function useThemeForm({ data }: ThemeFormProps): UseThemeFormReturn {
  const {
    colors: { buttonsLinks, navBar, staffPickTag, discountTag, productCardButton },
    mobile,
    radius: { default: defaultRadius },
  } = data;

  const defaultValues = {
    buttonsLinks,
    navBar,
    defaultRadius,
    mobileHeaderBackgroundColor: mobile?.headerBackgroundColor ?? buttonsLinks,
    mobileHeaderImageUrl: mobile?.headerImageUrl ?? '',
    mobileNavigationBarActiveColor: mobile?.bottomBarActiveColor ?? '',
    mobileNavigationBarBackgroundColor: mobile?.bottomBarBackgroundColor ?? '',
    mobileNavigationBarInactiveColor: mobile?.bottomBarInactiveColor ?? '',
    mobileTabLabelShop: mobile?.tabLabels?.shop ?? '',
    mobileTabLabelSpecials: mobile?.tabLabels?.specials ?? '',
    mobileTabLabelCart: mobile?.tabLabels?.cart ?? '',
    mobileTabLabelPay: mobile?.tabLabels?.pay ?? '',
    mobileTabLabelAccount: mobile?.tabLabels?.account ?? '',
    // Need to account for flag being off
    staffPickTag: staffPickTag ?? '',
    discountTag: discountTag ?? '',
    productCardButton,
  };

  const {
    control,
    formState: { isDirty },
    handleSubmit,
    reset,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    watch,
  } = useForm<FormValues>({ defaultValues });

  const { id: dispensaryId } = useParams<{ id: string }>();

  const showErnie = useErnie();

  const updateData = useUpdateWebCustomizationSettingsV2();

  const onSubmit: SubmitHandler<FormValues> = async (submittedValues) => {
    const input = formatInput(submittedValues);

    try {
      await updateData({
        variables: {
          dispensaryId,
          input,
        },
      });

      showErnie('Your theme has been updated', 'success');
      reset({ ...submittedValues });
    } catch (err) {
      showErnie('Something went wrong, please try again.', 'danger');
      console.error(err);
    }
  };

  const handlePublish = handleSubmit(onSubmit);

  const handleReset = (): void => reset();

  const formValues = watch();

  const previewData = useMergeDataWithFormValues(data, formValues);

  const applyAllInput = formatInput(formValues);
  const afterApplyAll = (): void => {
    reset({ ...formValues });
  };

  return {
    control,
    handlePublish,
    handleReset,
    isDirty,
    previewData,
    applyAllInput,
    afterApplyAll,
  };
}
